<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspaceScenesPanel.vue: the left panel listing scenes, 12rem
  wide, collapsible to a 3rem rail (always the rail below lg), New Scene, Fullscreen at the bottom. Lite has one scene,
  "1. Scene", always selected; New Scene opens the teaser (no reorder, rename, duplicate or delete).
-->
<script setup lang="ts">
import { computed, watch } from 'vue';
import { useFullscreen, useLocalStorage } from '@vueuse/core';
import Button from 'primevue/button';
import { useDevice } from '@/composables/device';

const emit = defineEmits<{ teaser: [event: Event] }>();

const device = useDevice();
const scenesCollapsed = useLocalStorage<boolean>('nanome-lite-device.scenesCollapsed', false);
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();
watch(isFullscreen, (v) => { if (v) scenesCollapsed.value = true; });

const isCollapsed = computed(() => scenesCollapsed.value || !device.breakpoints.lg.value);
const canExpand = computed(() => device.breakpoints.lg.value);

const scenes = [{ serial: 1, name: 'Scene' }];
</script>

<template>
  <div class="h-full flex flex-col shrink-0 bg-ground" :class="isCollapsed ? 'w-12' : 'w-48'" data-testid="scenes-panel">
    <div class="flex items-center" :class="isCollapsed ? 'justify-center' : 'justify-between p-2'">
      <span v-if="!isCollapsed" class="ml-1">Scenes</span>
      <Button
        v-if="canExpand"
        :class="{ 'w-full h-12 rd-0': isCollapsed }"
        :icon="isCollapsed ? 'i-tabler-chevrons-right' : 'i-tabler-chevrons-left'"
        :aria-label="isCollapsed ? 'Expand scenes' : 'Collapse scenes'"
        severity="secondary"
        size="small"
        rounded
        text
        data-testid="scenes-toggle"
        @click="scenesCollapsed = !scenesCollapsed"
      />
    </div>

    <div class="min-h-0 of-y-auto">
      <Button
        v-for="(scene, i) in scenes"
        :key="scene.serial"
        v-tooltip.right="scene.name"
        class="group h-12 grow-1 text-left truncate rd-0 p-button-selected"
        :class="isCollapsed ? 'w-12 p-1 justify-center' : 'w-full p-3 justify-between'"
        severity="secondary"
        size="small"
      >
        <div class="w-full flex flex-col min-w-0">
          <div :class="{ 'text-center': isCollapsed }" class="truncate">
            {{ isCollapsed ? i + 1 : `${i + 1}. ${scene.name}` }}
          </div>
        </div>
      </Button>
    </div>

    <Button
      v-tooltip.right="isCollapsed ? 'New Scene' : ''"
      :class="{ 'justify-start pl-4': !isCollapsed }"
      :label="isCollapsed ? undefined : 'New Scene'"
      class="w-full h-12 rd-0"
      icon="i-tabler-plus"
      severity="secondary"
      size="small"
      rounded
      text
      data-testid="scenes-new"
      @click="emit('teaser', $event)"
    />

    <Button
      v-tooltip.right="isCollapsed ? (isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen') : ''"
      :class="{ 'justify-start pl-4': !isCollapsed }"
      :label="isCollapsed ? undefined : isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'"
      :icon="isFullscreen ? 'i-tabler-arrows-diagonal-minimize-2' : 'i-tabler-arrows-diagonal'"
      class="w-full h-12 mt-auto rd-0"
      severity="secondary"
      size="small"
      rounded
      text
      @click="toggleFullscreen()"
    />
  </div>
</template>
